import * as React from "react";
import { format } from "date-fns";
import { CircleCheck } from "lucide-react";
import { usePostFlowContext } from "@/components/PostDialogs/SharedPostFlowContext";
import { Button } from "@/components/ui";
import { socialNetworkNames } from "@/interfaces";
import { promptSpecificDate } from "@/utils";
import { firstNameOf } from "./PostOptions";

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export interface SharePostedViewProps {
  postId: number | null;
  onViewPost: (postId: number) => void;
  onDone: () => void;
}

export const SharePostedView: React.FC<SharePostedViewProps> = ({ postId, onViewPost, onDone }) => {
  const { deliveryOptions, onBehalfOf, effectivePlayer } = usePostFlowContext();
  const networks = joinNames(deliveryOptions.socialNetworks.map(sn => socialNetworkNames[sn] ?? sn));
  const scheduled = deliveryOptions.sendMode === "SCHEDULED" && deliveryOptions.date && deliveryOptions.time;
  const when = scheduled ? promptSpecificDate(deliveryOptions.date!, deliveryOptions.time!) : null;
  const whenLabel = when ? format(new Date(when), "MMM d, h:mm a") : "later";

  let title = `On its way to ${networks}`;
  let description = "It'll show up on your profiles shortly.";
  if (onBehalfOf) {
    const first = firstNameOf(effectivePlayer?.fullName) ?? "the player";
    title = `On its way to ${first}'s ${networks}`;
    description = "You can find it on the calendar.";
    if (!deliveryOptions.isAutoPost) {
      title = `Sent to ${first}`;
      description = "They'll see it in their app and can post it whenever they're ready.";
    } else if (scheduled) {
      title = `Scheduled for ${first} on ${whenLabel}`;
    }
  } else if (!deliveryOptions.isAutoPost) {
    title = "Sent to your phone";
    description = "Open SocialCoach on your phone to post it.";
  } else if (scheduled) {
    title = `Scheduled for ${whenLabel}`;
    description = `We'll post it to ${networks} for you.`;
  }

  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="share-posted">
      <CircleCheck className="size-14 text-emerald-600" />
      <h3 className="text-xl font-bold text-slate-900">{title}</h3>
      <p className="max-w-sm text-sm text-slate-500">{description}</p>
      <div className="mt-4 flex gap-2">
        {postId != null && (
          <Button variant="outline" onClick={() => onViewPost(postId)}>
            View post
          </Button>
        )}
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  );
};
